/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, ChangeDetectionStrategy, Self, Optional, ChangeDetectorRef } from '@angular/core';
import { TicketEditBaseComponent } from '../ticket-edit-base/ticket-edit-base.component';
import { UntypedFormControl, NgControl, Validators } from '@angular/forms';
import { MultiWrapperComponent } from '../multi-wrapper/multi-wrapper.component';
import { IProcoreRFI, ProcoreService } from 'src/app/services/procore.service';
import { TicketEditService } from '../../ticket-edit.service';
import { filter, take, switchMap, takeUntil, exhaustMap } from 'rxjs/operators';
import { EditTicketsCombined } from 'src/app/app-data/models/EditTicketsCombined';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { LoadingBlockerState } from 'src/app/common/utility-components/loading-blocker';
import * as strings from '@touchplan/strings';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService } from '@touchplan/logging';
import { RouteDataStateService } from 'src/app/services/route-data.state.service';
import { ProjectLegacy } from 'src/app/app-data/models/Project';

@Component({
	selector: 'ticket-procore-link',
	templateUrl: './ticket-procore-link.component.html',
	styles: [],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [LoggerServiceFactory.provide('TicketProcoreLinkComponent')],
})
export class TicketProcoreLinkComponent extends TicketEditBaseComponent {
	public subjectControl: UntypedFormControl = new UntypedFormControl('', {
		validators: [Validators.required],
	});
	public descriptionControl: UntypedFormControl = new UntypedFormControl('', {
		validators: [Validators.required],
	});
	public rfiId: string;
	public loadingObservable: Subject<LoadingBlockerState> = new Subject();
	public itemsObservable: BehaviorSubject<Map<string, any>> = new BehaviorSubject(new Map());
	private etc: EditTicketsCombined;

	constructor(
		@Self() @Optional() public control: NgControl,
		@Optional() public multiWrapper: MultiWrapperComponent,
		@Optional() protected cd: ChangeDetectorRef,
		private ticketEditService: TicketEditService,
		public procoreService: ProcoreService,
		private routeDataState: RouteDataStateService,
		private logger: DefaultLoggerService
	) {
		super(control, multiWrapper, cd);
	}

	public ngOnInit() {
		super.ngOnInit();

		this.internalField.valueChanges.pipe(takeUntil(this.shutdownSubject)).subscribe((val: any) => {
			this.subjectControl.setValue(val.subject ? val.subject : '');
			this.descriptionControl.setValue(val.questions && val.questions[0] ? val.questions[0].body : '');
			this.rfiId = val.id ? val.id : null;

			if (this.rfiId) {
				this.disableForm();
			} else {
				this.enableForm();
			}

			this.onChange(this.rfiId);
		});

		// Retrieve the list of project RFIs, then create a SelectItem[] with those items
		this.getActivatedRouteProjectData$()
			.pipe(
				switchMap((projectData) =>
					this.procoreService.listRFIs(projectData.procoreProjectId, projectData.procoreCompanyId)
				)
			)
			.subscribe((rfiList: IProcoreRFI[]) => {
				if (rfiList) {
					this.createRFIList(rfiList);
				}
			});
		// Subscribe to the current set of data
		this.ticketEditService.combinedEdit$
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe((etc: EditTicketsCombined) => {
				if (etc) {
					this.rfiId = etc.data.procoreRfiId;
					this.etc = etc;
					this.loadingObservable.next(LoadingBlockerState.complete);
				}
			});

		this.loadingObservable.next(LoadingBlockerState.inProgress);
	}

	private disableForm(): void {
		this.subjectControl.disable();
		this.descriptionControl.disable();
	}

	private enableForm(): void {
		this.subjectControl.enable();
		this.descriptionControl.enable();
	}

	private createRFIList(rfiList: any[]): void {
		const itemsMap = new Map().set('Create New', {});

		rfiList.forEach((rfi, idx) => {
			itemsMap.set(rfi.subject, rfi);
			if (rfi.id === this.rfiId) {
				this.internalField.setValue(rfi);
			}
		});

		this.itemsObservable.next(itemsMap);
	}

	public close(): void {
		this.ticketEditService.setOverlay(null);
	}

	/**
	 * Custom sorting function for the RFI dropdown:
	 * makes sure "Create New" is always the first option, and
	 * otherwise sorts the other options alphabetically.
	 */
	public compareRfiDropdownOptions(a: string, b: string) {
		if (a.localeCompare(strings.PROCORE_CREATE_NEW) === 0) {
			return -1;
		}
		if (b.localeCompare(strings.PROCORE_CREATE_NEW) === 0) {
			return 1;
		}
		return a.localeCompare(b);
	}

	public linkRFI(rfiId: string): void {
		this.internalField.setValue(rfiId);

		this.ticketEditService.writeCombinationBackToList(this.etc, { procoreRfiId: rfiId });
		this.ticketEditService.setOverlay(null);
		// mark the ticket edit form dirst now that Link to Procore changed
		this.ticketEditService.markFormAsDirty();
	}

	public saveRFI(): void {
		// Create an RFI and then link...or just link?
		if (this.rfiId) {
			this.linkRFI(this.rfiId);
		} else {
			this.createRFI();
		}
	}

	public createRFI() {
		if (this.subjectControl.invalid || this.descriptionControl.invalid) {
			return Logging.warning(strings.PROCORE_PLEASE_SELECT_AN_RFI);
		}

		this.loadingObservable.next(LoadingBlockerState.inProgress);

		this.getActivatedRouteProjectData$()
			.pipe(
				exhaustMap((projectData) =>
					this.procoreService.createNewRFI(
						{
							subject: this.subjectControl.value,
							questionBody: this.descriptionControl.value,
						},
						projectData.procoreProjectId,
						projectData.procoreCompanyId
					)
				)
			)
			.subscribe((newRFI: IProcoreRFI) => {
				this.logger.log(newRFI);
				if (newRFI) {
					this.logger.log('NEW RFI IS', newRFI);
					this.loadingObservable.next(LoadingBlockerState.complete);
					this.linkRFI(newRFI.id);
				}
			});
	}

	private getActivatedRouteProjectData$(): Observable<ProjectLegacy> {
		return this.routeDataState.projectData$.pipe(
			filter((projectData) => !!projectData),
			take(1)
		);
	}
}
